import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Play, Heart, Clock, Globe } from 'lucide-react';
import { CountdownReveal } from '../CountdownReveal';
import { WaitlistForm } from './WaitlistForm';
import { AnimatedCount } from './AnimatedCount';
import { FADE_UP, SCALE_IN } from './data';

export const Hero = ({ signupCount, onJoined, onOpenVideo }) => (
  <section className="relative overflow-hidden">
    <div className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />
    <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 py-16 md:grid-cols-12 md:px-12 md:py-24 lg:gap-20">
      <motion.div {...FADE_UP} transition={{ duration: 0.7 }} className="md:col-span-5">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-muted-foreground shadow-sm">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          UNWP — say it <span className="font-semibold text-foreground">"un-wrap"</span> · early access limited to 500
        </div>
        <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Turn any moment into an
          <span className="text-primary"> unforgettable reveal.</span>
        </h1>
        <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
          First the hint. Then the countdown. Then the gasp, the grin, the
          <span className="font-semibold text-foreground"> happy tears</span>. UNWP wraps your
          photos, videos and words in a mystery countdown — a surprise they'll never forget.
          Want to add a gift card too? You can. It's optional, always.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2.5 text-sm font-semibold text-foreground" data-testid="hero-reach-strip">
          <span className="flex items-center gap-2"><Heart className="h-4 w-4 text-primary" /> To anyone you love</span>
          <span className="h-4 w-px bg-border" aria-hidden />
          <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Anytime</span>
          <span className="h-4 w-px bg-border" aria-hidden />
          <span className="flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /> Anywhere in the world</span>
        </div>

        <div className="mt-8">
          <WaitlistForm idPrefix="hero" onJoined={onJoined} />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-2" data-testid="signup-counter">
            <span className="relative flex h-2 w-2 items-center justify-center">
              <span className="absolute h-2 w-2 animate-ping rounded-full bg-primary/60" />
              <span className="h-2 w-2 rounded-full bg-primary" />
            </span>
            {signupCount > 0 ? (
              <span><strong className="text-foreground"><AnimatedCount target={signupCount} /></strong> already joined</span>
            ) : (
              <span>Be among the very first to join</span>
            )}
          </span>
          <span data-testid="hero-no-app-hint">No app needed — works in any browser</span>
          <span data-testid="hero-giftcard-optional" className="font-semibold text-foreground">Gift card optional</span>
        </div>

        <button
          onClick={onOpenVideo}
          data-testid="hero-see-demo"
          className="group mt-7 flex items-center gap-4 rounded-2xl border border-border bg-card p-2.5 pr-6 text-left shadow-sm transition-shadow hover:shadow-lg"
        >
          <span className="relative block h-16 w-16 shrink-0 overflow-hidden rounded-xl">
            <img src="/unwp-ad-poster.jpg" alt="UNWP social film" className="h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/15">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow transition-transform group-hover:scale-110">
                <Play className="h-3.5 w-3.5 fill-primary text-primary" />
              </span>
            </span>
          </span>
          <span>
            <span className="block text-sm font-bold text-foreground">Watch our 50-second film</span>
            <span className="block text-xs text-muted-foreground">The ad that's got people talking</span>
          </span>
        </button>
      </motion.div>

      <motion.div {...SCALE_IN} transition={{ duration: 0.8, delay: 0.15 }} className="md:col-span-7">
        <CountdownReveal />
      </motion.div>
    </div>
  </section>
);
